import { useState, useCallback } from 'react';

export interface WidgetPrefs {
  showList: boolean;
  sortBy: 'title' | 'created' | 'updated';
}

const DEFAULTS: WidgetPrefs = {
  showList: true,
  sortBy: 'updated',
};

function loadPrefs(key: string): WidgetPrefs {
  try {
    const raw = localStorage.getItem(`widget-prefs:${key}`);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULTS };
}

function savePrefs(key: string, prefs: WidgetPrefs) {
  try {
    localStorage.setItem(`widget-prefs:${key}`, JSON.stringify(prefs));
  } catch { /* ignore */ }
}

/**
 * Persists widget view preferences (list/grid, sort order) to localStorage.
 * Keyed by the widget's dataKey so each widget instance remembers its own prefs.
 */
export function useWidgetPrefs(dataKey: string) {
  const [prefs, setPrefs] = useState<WidgetPrefs>(() => loadPrefs(dataKey));

  const updatePrefs = useCallback((partial: Partial<WidgetPrefs>) => {
    setPrefs(prev => {
      const next = { ...prev, ...partial };
      savePrefs(dataKey, next);
      return next;
    });
  }, [dataKey]);

  return {
    showList: prefs.showList,
    setShowList: (v: boolean) => updatePrefs({ showList: v }),
    sortBy: prefs.sortBy,
    setSortBy: (v: WidgetPrefs['sortBy']) => updatePrefs({ sortBy: v }),
  };
}
